/**
 * Configure Wizard — final CTA copy driven by product mode (UI only).
 * Reuses existing portfolio / status signals — no new business rules.
 */
import type { ProductCard } from "@itaas/pep-contracts";
import { isPrelaunchProduct } from "../utils/product-status";

export type ConfigureFinalCtaMode = "self_service" | "assisted" | "early_access";

export interface ConfigureFinalCtaView {
  mode: ConfigureFinalCtaMode;
  /** Form section title */
  title: string;
  /** Primary submit button label */
  submitLabel: string;
  /** Optional subtitle under the title (self-service) */
  subtitle: string | null;
}

/**
 * Map primary product signals → CTA mode.
 *
 * Existing signals only:
 * - `portfolio.trialAllowed === true` → SELF_SERVICE
 * - `portfolio.trialAllowed === false` + quote → ASSISTED
 * - prelaunch / no quote → EARLY_ACCESS
 * - missing portfolio → prelaunch status or commercial assisted default
 */
export function resolveConfigureFinalCtaMode(
  product?: ProductCard | null,
): ConfigureFinalCtaMode {
  if (!product) return "assisted";

  const portfolio = product.portfolio;
  if (!portfolio) {
    return isPrelaunchProduct(product.status) ? "early_access" : "assisted";
  }

  if (portfolio.trialAllowed === true) return "self_service";

  // trialAllowed === false
  if (portfolio.quoteAllowed === true || portfolio.allowedCtas?.includes("QUOTE")) {
    return "assisted";
  }

  return "early_access";
}

export function toConfigureFinalCtaView(
  product: ProductCard | null | undefined,
  locale: "fr" | "en",
): ConfigureFinalCtaView {
  const mode = resolveConfigureFinalCtaMode(product);

  if (mode === "self_service") {
    return {
      mode,
      title:
        locale === "en" ? "Create my environment" : "Créer mon environnement",
      submitLabel:
        locale === "en" ? "Create my environment" : "Créer mon environnement",
      subtitle:
        locale === "en"
          ? "Your environment will be created immediately.\nYou will receive your access details by email."
          : "Votre environnement sera créé immédiatement.\nVous recevrez vos accès par e-mail.",
    };
  }

  if (mode === "early_access") {
    return {
      mode,
      title: locale === "en" ? "Join the waitlist" : "Rejoindre la liste d'attente",
      submitLabel:
        locale === "en" ? "Join the waitlist" : "Rejoindre la liste d'attente",
      subtitle: null,
    };
  }

  return {
    mode: "assisted",
    title: locale === "en" ? "Request a quote" : "Demander un devis",
    submitLabel: locale === "en" ? "Request a quote" : "Demander un devis",
    subtitle: null,
  };
}
